/**
 * Modos de runtime: normal | pressure | degraded | recovery | safe_mode
 */

import { getUnifiedPressureLevel } from './pressureModel';
import { isStorageSafeMode } from '../storage/storageSafeMode';
import { recordRuntimeModeChange } from '../telemetry';

export type RuntimeMode =
  | 'normal'
  | 'pressure'
  | 'degraded'
  | 'recovery'
  | 'safe_mode';

let mode: RuntimeMode = 'normal';
let recoveryActive = false;
let explicitMode: RuntimeMode | null = null;

export function setRecoveryModeActive(active: boolean): void {
  recoveryActive = active;
  updateRuntimeMode();
}

export function setRuntimeModeExplicit(next: RuntimeMode): void {
  explicitMode = next;
  setMode(next);
}

export function clearRuntimeModeExplicit(): void {
  explicitMode = null;
  updateRuntimeMode();
}

export function updateRuntimeMode(): RuntimeMode {
  if (explicitMode === 'safe_mode' || isStorageSafeMode()) {
    setMode('safe_mode');
    return mode;
  }
  if (explicitMode) {
    setMode(explicitMode);
    return mode;
  }
  if (recoveryActive) {
    setMode('recovery');
    return mode;
  }
  const p = getUnifiedPressureLevel();
  if (p >= 0.85) setMode('degraded');
  else if (p >= 0.55) setMode('pressure');
  else setMode('normal');
  return mode;
}

function setMode(next: RuntimeMode): void {
  if (mode !== next) {
    mode = next;
    recordRuntimeModeChange(next);
  }
}

export function getRuntimeMode(): RuntimeMode {
  return mode;
}

export function isUnsafeMutationAllowed(): boolean {
  updateRuntimeMode();
  return mode !== 'recovery' && mode !== 'safe_mode';
}

export function canRunFlush(): boolean {
  updateRuntimeMode();
  return mode !== 'safe_mode' && !isStorageSafeMode();
}

export function canRunReplay(): boolean {
  updateRuntimeMode();
  return (
    mode !== 'safe_mode' &&
    mode !== 'recovery' &&
    !isStorageSafeMode()
  );
}

export function _testSetRuntimeMode(m: RuntimeMode): void {
  mode = m;
  explicitMode = m;
}

export function _testResetRuntimeMode(): void {
  mode = 'normal';
  recoveryActive = false;
  explicitMode = null;
}
